import { useEffect, useState } from "react";
import { useParams, useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  Download,
  CalendarPlus,
  Home,
  Loader2,
  AlertCircle,
  Radio,
  Check,
} from "lucide-react";
import { useTenant } from "@/contexts/TenantContext";
import { publicApi } from "@/lib/api";
import { trackPageView } from "@/lib/analytics";
import { useDateLocale } from "@/lib/dateLocale";
import { SEO, SEOPresets } from "@/components/shared/SEO";
import { EnhancedHeader } from "@/components/layout/EnhancedHeader";
import { formatInTimezone, DEFAULT_TIMEZONE } from "@shared/timezone-utils";

interface TicketData {
  ticketNumber: string;
  status: string;
  passengerName: string;
  seatNumber: string;
  qrCodeUrl: string;
  trip: {
    id: number;
    departureTime: string;
    arrivalTime: string | null;
    routeName: string | null;
    originCity: string | null;
    destinationCity: string | null;
    /**
     * The PASSENGER's own boarding/alighting, pre-formatted server-side in each
     * stop city's zone by the same resolver the printed ticket uses (PR B3).
     * Null when that stop's time is genuinely unknown — never a substituted
     * clock. Older responses omit these; the pages fall back to the raw trip
     * times so a cached client keeps working.
     */
    boardingTimeFormatted?: string | null;
    alightingTimeFormatted?: string | null;
    /** Raw instants for this passenger's own leg (calendar export). */
    boardingIso?: string | null;
    alightingIso?: string | null;
    boardingAbbr?: string | null;
    alightingAbbr?: string | null;
    boardingBasis?: "passenger" | "route";
    alightingBasis?: "passenger" | "route";
    /** Instant + zone the travel DATE should be rendered from. */
    dateInstant?: string | null;
    dateTimezone?: string | null;
    /** "route" = the service's own date, not this passenger's. Must be labelled. */
    dateBasis?: "passenger" | "route";
  };
}

export function ConfirmationPage() {
  const { t } = useTranslation();
  const dl = useDateLocale();
  const params = useParams<{ ticketNumber: string }>();
  const ticketNumber = params.ticketNumber || "";
  const { slug, tenant } = useTenant();
  const [, setLocation] = useLocation();
  const [downloadingTicket, setDownloadingTicket] = useState(false);

  const timezone = tenant?.timezone || DEFAULT_TIMEZONE;

  useEffect(() => {
    if (slug) trackPageView("confirmation", slug);
  }, [slug]);

  const {
    data: ticket,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["ticket", slug, ticketNumber],
    queryFn: async () => {
      if (!slug || !ticketNumber) return null;
      return publicApi.get<TicketData>(
        `/api/public/${slug}/tickets/${ticketNumber}`,
      );
    },
    enabled: !!slug && !!ticketNumber,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const { data: trackingData } = useQuery({
    queryKey: ["trip-tracking", slug, ticket?.trip.id],
    queryFn: async () => {
      if (!slug || !ticket?.trip.id) return null;
      return publicApi.get<{ active: boolean; sessionId: string | null }>(
        `/api/public/${slug}/trips/${ticket.trip.id}/tracking`,
      );
    },
    enabled: !!slug && !!ticket?.trip.id,
    refetchInterval: 60000,
  });

  // Prefer the server-resolved passenger times (PR B3): boarding and alighting
  // in their OWN stop cities' zones, computed by the same resolver the PRINTED
  // ticket uses. Rendering the trip's raw departure in the tenant zone here is
  // what made one booking show two different times depending on where the
  // passenger looked.
  const departureDate = ticket?.trip.dateInstant
    ? formatInTimezone(
        ticket.trip.dateInstant,
        ticket.trip.dateTimezone || timezone,
        dl.pattern("weekdayDateYearDot"),
        dl.locale,
      )
    : ticket?.trip.departureTime
      ? formatInTimezone(
          ticket.trip.departureTime,
          timezone,
          dl.pattern("weekdayDateYearDot"),
          dl.locale,
        )
      : "";
  // `in` rather than `??`: the server always sends this key now, and an
  // explicit null means "this passenger's boarding time is genuinely unknown".
  // Coalescing it to the route departure is exactly the substituted clock the
  // resolver refuses to produce.
  const hasResolvedTimes =
    !!ticket?.trip && "boardingTimeFormatted" in ticket.trip;
  const departureTime = hasResolvedTimes
    ? (ticket!.trip.boardingTimeFormatted ?? "—")
    : ticket?.trip.departureTime
      ? formatInTimezone(ticket.trip.departureTime, timezone, "HH:mm")
      : "";
  const departureAbbr = ticket?.trip.boardingAbbr ?? null;
  // When the boarding instant is unknown the date shown is the SERVICE's,
  // not this passenger's. The printed ticket labels that; so does this.
  const dateIsRouteBasis =
    hasResolvedTimes && ticket?.trip.dateBasis === "route";
  const arrivalTime = hasResolvedTimes
    ? (ticket!.trip.alightingTimeFormatted ?? "")
    : ticket?.trip.arrivalTime
      ? formatInTimezone(ticket.trip.arrivalTime, timezone, "HH:mm")
      : "";
  const arrivalAbbr = ticket?.trip.alightingAbbr ?? null;

  // Only offered when the calendar event would describe THIS passenger's own
  // journey (see handleAddToCalendar).
  const canAddToCalendar = hasResolvedTimes
    ? ticket?.trip.boardingBasis === "passenger" && !!ticket?.trip.boardingIso
    : !!ticket?.trip.departureTime;

  const handleAddToCalendar = () => {
    // The PASSENGER's own leg, not the whole route's — an intermediate boarder
    // was getting a calendar entry for a journey they are not making.
    if (!ticket) return;
    // Explicit null means "this passenger's boarding time is unknown", which is
    // NOT the same as an old response that never carried the field. Coalescing
    // exported the route's departure as the rider's journey — the same
    // substituted clock the resolver refuses to produce.
    // Only export a calendar event for a time we can attribute to THIS
    // passenger. A segment-less booking resolves to the route's departure with
    // basis "route": the ticket shows it labelled, but a calendar entry has
    // nowhere to put the label, so it would assert a journey we cannot stand
    // behind.
    const startIso =
      hasResolvedTimes
        ? ticket.trip.boardingBasis === "passenger"
          ? ticket.trip.boardingIso
          : null
        : ticket.trip.departureTime;
    if (!startIso) return;
    const startDate = new Date(startIso);
    const endIso =
      hasResolvedTimes
        ? ticket.trip.alightingBasis === "passenger"
          ? ticket.trip.alightingIso
          : null
        : ticket.trip.arrivalTime;
    // No invented end. The previous "+4 hours" was a guess at a journey length
    // on a route that runs closer to forty; a calendar entry with no end is
    // honest, one with a wrong end is not.
    const endDate = endIso ? new Date(endIso) : null;

    const formatICSDate = (d: Date) =>
      d.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";

    const origin = ticket.trip.originCity || "Origin";
    const destination = ticket.trip.destinationCity || "Destination";
    const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//RutaLink//Booking//EN
BEGIN:VEVENT
UID:${ticket.ticketNumber}@rutalink.cloud
DTSTAMP:${formatICSDate(new Date())}
DTSTART:${formatICSDate(startDate)}
${endDate ? `DTEND:${formatICSDate(endDate)}
` : ""}SUMMARY:Bus Trip: ${origin} → ${destination}
DESCRIPTION:Ticket: ${ticket.ticketNumber}\\nPassenger: ${ticket.passengerName}\\nSeat: ${ticket.seatNumber}
LOCATION:${origin}
END:VEVENT
END:VCALENDAR`;

    const blob = new Blob([icsContent], {
      type: "text/calendar;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `rutalink-trip-${ticket.ticketNumber}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleDownloadTicket = async () => {
    if (!ticket?.qrCodeUrl) return;
    setDownloadingTicket(true);
    try {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      canvas.width = 400;
      canvas.height = 600;
      ctx.fillStyle = "#FBFAF7";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      ctx.fillStyle = "#1C1A15";
      ctx.font = "600 22px 'Fraunces', Georgia, serif";
      ctx.textAlign = "center";
      ctx.fillText(tenant?.name || "RutaLink", canvas.width / 2, 42);

      const qrImage = new Image();
      qrImage.crossOrigin = "anonymous";
      await new Promise<void>((resolve, reject) => {
        qrImage.onload = () => resolve();
        qrImage.onerror = reject;
        qrImage.src = ticket.qrCodeUrl;
      });
      ctx.drawImage(qrImage, (canvas.width - 200) / 2, 70, 200, 200);

      ctx.font =
        "500 13px 'JetBrains Mono', 'SF Mono', Menlo, monospace";
      ctx.fillStyle = "#4A453A";
      ctx.fillText(ticket.ticketNumber, canvas.width / 2, 295);

      ctx.strokeStyle = "#D8D3C6";
      ctx.setLineDash([5, 3]);
      ctx.beginPath();
      ctx.moveTo(40, 320);
      ctx.lineTo(canvas.width - 40, 320);
      ctx.stroke();
      ctx.setLineDash([]);

      const origin = ticket.trip.originCity || "Origin";
      const destination = ticket.trip.destinationCity || "Destination";

      ctx.textAlign = "left";
      ctx.font = "500 11px 'Inter Tight', system-ui, sans-serif";
      ctx.fillStyle = "#8B8576";

      const writeField = (label: string, value: string, y: number) => {
        ctx.font = "500 11px 'Inter Tight', system-ui, sans-serif";
        ctx.fillStyle = "#8B8576";
        ctx.fillText(label.toUpperCase(), 40, y);
        ctx.font = "500 16px 'Inter Tight', system-ui, sans-serif";
        ctx.fillStyle = "#1C1A15";
        ctx.fillText(value, 40, y + 18);
      };

      writeField("Route", `${origin} → ${destination}`, 350);
      writeField(
        "Date & Time",
        `${departureDate}${dateIsRouteBasis ? ` ${t("trips.routeDeparture", "(route departure)")}` : ""}${departureTime ? `  ${departureTime}${departureAbbr ? ` ${departureAbbr}` : ""}` : ""}${arrivalTime ? ` – ${arrivalTime}${arrivalAbbr ? ` ${arrivalAbbr}` : ""}` : ""}`,
        400,
      );
      writeField("Passenger", ticket.passengerName, 450);
      writeField("Seat", ticket.seatNumber, 500);

      ctx.textAlign = "center";
      ctx.font = "500 10px 'Inter Tight', system-ui, sans-serif";
      ctx.fillStyle = "#8B8576";
      ctx.fillText("Powered by RutaLink", canvas.width / 2, canvas.height - 22);

      const dataUrl = canvas.toDataURL("image/png");
      const link = document.createElement("a");
      link.href = dataUrl;
      link.download = `ticket-${ticket.ticketNumber}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error("Failed to download ticket:", err);
    } finally {
      setDownloadingTicket(false);
    }
  };

  const handleBookAnother = () => setLocation("/");

  if (isLoading) {
    return (
      <div className="min-h-screen bg-stone-50 text-ink flex flex-col">
        <EnhancedHeader />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="h-10 w-10 animate-spin text-ink-muted mx-auto mb-4" />
            <p className="text-ink-muted text-sm">{t("common.loading")}</p>
          </div>
        </main>
      </div>
    );
  }

  if (error || !ticket) {
    return (
      <div className="min-h-screen bg-stone-50 text-ink flex flex-col">
        <EnhancedHeader />
        <main className="flex-1 flex items-center justify-center px-6 py-12">
          <div className="max-w-md text-center">
            <AlertCircle className="h-10 w-10 text-destructive mx-auto mb-4" />
            <h2 className="font-display text-display-md text-ink mb-2">
              {t("confirmation.notFound")}
            </h2>
            <p className="text-ink-muted mb-6">
              {t("confirmation.notFoundDescription")}
            </p>
            <button
              onClick={handleBookAnother}
              className="px-5 py-2.5 bg-ink text-stone-50 hover:bg-brand transition-colors focus-visible-ring"
            >
              {t("errors.goHome")}
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 text-ink flex flex-col">
      <SEO {...SEOPresets.confirmation} />

      <EnhancedHeader />

      <main id="main-content" className="flex-1">
        <div className="mx-auto max-w-2xl px-6 py-10 md:py-14">
          {/* Success eyebrow */}
          <div className="flex items-center justify-center gap-3 mb-6 animate-fade-up">
            <span className="h-px w-8 bg-hairline-strong" />
            <span className="inline-flex items-center gap-2 text-eyebrow uppercase text-ink-muted">
              <Check className="h-3.5 w-3.5 text-brand" aria-hidden="true" />
              {t("confirmation.title")}
            </span>
            <span className="h-px w-8 bg-hairline-strong" />
          </div>

          {/* Boarding-pass ticket */}
          <article
            className="relative bg-white border hairline-strong shadow-ticket animate-fade-up"
            style={{ animationDelay: "80ms" }}
          >
            {/* Label strip */}
            <header className="flex items-center justify-between px-6 py-3.5 border-b hairline-strong bg-stone-50/60">
              <div className="flex items-center gap-2.5 min-w-0">
                <span className="h-2 w-2 bg-brand flex-shrink-0" aria-hidden="true" />
                <span className="font-display text-[1.05rem] text-ink truncate">
                  {tenant?.name}
                </span>
              </div>
              <span className="text-eyebrow uppercase text-brand tabular">
                {t(`ticket.status.${ticket.status}`, ticket.status)}
              </span>
            </header>

            {/* Main body + QR stub — two-column on md+ */}
            <div className="grid md:grid-cols-[1fr_auto]">
              {/* Body */}
              <div className="px-6 py-6 md:py-8 min-w-0">
                {/* Route headline */}
                <div>
                  <div className="text-eyebrow uppercase text-ink-subtle">
                    {t("trips.departure")}
                  </div>
                  <h2 className="font-display text-[1.75rem] md:text-[2.15rem] leading-tight text-ink mt-1 truncate">
                    {ticket.trip.originCity}
                  </h2>
                  <div
                    className="flex items-center gap-1 my-3 max-w-[12rem]"
                    aria-hidden="true"
                  >
                    <span className="h-1.5 w-1.5 rounded-full bg-ink flex-shrink-0" />
                    <span className="flex-1 h-px bg-hairline-strong" />
                    <span className="h-1.5 w-1.5 rounded-full bg-brand flex-shrink-0" />
                  </div>
                  <div className="text-eyebrow uppercase text-ink-subtle">
                    {t("trips.arrival")}
                  </div>
                  <h2 className="font-display text-[1.75rem] md:text-[2.15rem] leading-tight text-ink mt-1 truncate">
                    {ticket.trip.destinationCity}
                  </h2>
                </div>

                {/* Detail grid */}
                <div className="mt-6 grid grid-cols-2 gap-x-4 gap-y-4 pt-5 border-t hairline">
                  <Field
                    label={t("trips.departure")}
                    value={
                      dateIsRouteBasis
                        ? `${departureDate} ${t("trips.routeDeparture", "(route departure)")}`
                        : departureDate
                    }
                  />
                  <Field
                    label="Time"
                    value={
                      departureTime
                        ? `${departureTime}${departureAbbr ? ` ${departureAbbr}` : ""}${arrivalTime ? ` — ${arrivalTime}${arrivalAbbr ? ` ${arrivalAbbr}` : ""}` : ""}`
                        : "—"
                    }
                  />
                  <Field
                    label={t("search.passenger")}
                    value={ticket.passengerName}
                  />
                  <Field
                    label={t("confirmation.seat")}
                    value={ticket.seatNumber}
                  />
                </div>
              </div>

              {/* Perforated vertical divider on desktop */}
              <div
                className="hidden md:block w-px bg-hairline-strong relative"
                aria-hidden="true"
              >
                <span className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 h-4 w-4 rounded-full bg-stone-50 border hairline-strong" />
                <span className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 h-4 w-4 rounded-full bg-stone-50 border hairline-strong" />
              </div>

              {/* Horizontal perforation on mobile */}
              <div className="md:hidden relative h-4 perforation border-y hairline-strong" aria-hidden="true">
                <span className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 h-4 w-4 rounded-full bg-stone-50 border-r hairline-strong" />
                <span className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 h-4 w-4 rounded-full bg-stone-50 border-l hairline-strong" />
              </div>

              {/* QR stub */}
              <div className="px-6 py-6 md:py-8 flex flex-col items-center justify-center gap-3">
                {ticket.qrCodeUrl && (
                  <img
                    src={ticket.qrCodeUrl}
                    alt="Ticket QR Code"
                    className="w-40 h-40 md:w-44 md:h-44"
                    data-testid="confirmation-qr"
                  />
                )}
                <div className="text-center">
                  <div className="text-eyebrow uppercase text-ink-subtle">
                    {t("confirmation.ticketNumber", "Ticket")}
                  </div>
                  <div
                    className="font-mono-tabular text-sm mt-1 text-ink"
                    data-testid="confirmation-ticket-number"
                  >
                    {ticket.ticketNumber}
                  </div>
                </div>
              </div>
            </div>
          </article>

          {/* Ticket sent-to note */}
          <p className="text-center text-sm text-ink-muted mt-5">
            {t("confirmation.ticketSentTo", { contact: ticket.passengerName })}
          </p>

          {/* Action buttons */}
          <div
            className="mt-8 space-y-3 animate-fade-up"
            style={{ animationDelay: "160ms" }}
          >
            <div className="grid grid-cols-2 gap-3">
              {/* Hidden when we cannot attribute a boarding time to this
                  passenger — a button that silently does nothing is worse than
                  no button. */}
              {canAddToCalendar && (
                <ActionButton
                  onClick={handleAddToCalendar}
                  icon={<CalendarPlus className="h-4 w-4" />}
                  label={t("confirmation.addToCalendar")}
                />
              )}
              <ActionButton
                onClick={handleDownloadTicket}
                disabled={downloadingTicket || !ticket.qrCodeUrl}
                icon={
                  downloadingTicket ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4" />
                  )
                }
                label={t("confirmation.downloadTicket")}
              />
            </div>

            {trackingData?.active && trackingData.sessionId && (
              <button
                className="w-full h-14 bg-brand text-brand-ink font-semibold text-[0.85rem] uppercase tracking-[0.12em] inline-flex items-center justify-center gap-2 hover:bg-ink transition-[background] focus-visible-ring"
                onClick={() =>
                  window.open(`/track/${trackingData.sessionId}`, "_blank")
                }
              >
                <Radio className="h-4 w-4" />
                {t("confirmation.trackYourBus")}
              </button>
            )}

            <button
              onClick={handleBookAnother}
              className={`w-full h-12 inline-flex items-center justify-center gap-2 transition-colors focus-visible-ring ${
                trackingData?.active
                  ? "border hairline-strong text-ink hover:border-ink"
                  : "bg-ink text-stone-50 hover:bg-brand"
              }`}
            >
              <Home className="h-4 w-4" />
              {t("confirmation.bookAnother")}
            </button>
          </div>
        </div>
      </main>

      <footer className="border-t hairline-strong bg-white">
        <div className="mx-auto max-w-2xl px-6 py-5 flex items-center justify-between">
          <span className="text-xs text-ink-subtle">
            {t("common.poweredBy")}
          </span>
          <span className="text-xs text-ink-subtle tabular">
            © {new Date().getFullYear()} {tenant?.name}
          </span>
        </div>
      </footer>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <div className="text-eyebrow uppercase text-ink-subtle">{label}</div>
      <div className="font-medium text-ink text-sm mt-1 truncate">{value}</div>
    </div>
  );
}

function ActionButton({
  onClick,
  disabled,
  icon,
  label,
}: {
  onClick: () => void;
  disabled?: boolean;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="h-12 border hairline-strong text-ink hover:border-ink disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2 text-sm font-medium transition-colors focus-visible-ring"
    >
      {icon}
      {label}
    </button>
  );
}

export default ConfirmationPage;
